import '@babylonjs/core/Meshes/thinInstanceMesh';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_CENTER, BOARD_TOP_Y } from './constants';

export interface BoardFog {
  /** Shows or hides the smoke; hidden, it costs nothing. */
  setEnabled(on: boolean): void;
  /** How thick the smoke is: 0 is none, 1 stands the pieces in it up to the waist. */
  setDensity(density: number): void;
  /** Drifts the smoke; call every frame the board is showing. */
  update(dtSeconds: number): void;
  dispose(): void;
}

export const DEFAULT_FOG_DENSITY = 0.8;

/**
 * Heavy smoke lying over the board: two stacks of three translucent sheets, one dense stack on
 * the tiles and a thinner one up to the pieces' waists, so the pieces stand out of it. Each stack
 * is one mesh with three thin instances (two draw calls in all). The sheets of a stack share one
 * pattern, shifted a few centimetres per sheet, so they read as one soft body of smoke rather
 * than three unrelated veils; the two stacks are turned a quarter from each other. The material
 * multiplies two tileable noise textures scrolling in different directions, which makes the
 * smoke curl instead of sliding as a sheet. Alpha comes from the textures and a per-vertex edge
 * falloff; nothing writes depth, so pieces sort correctly and nothing is picked.
 */
interface Stack {
  name: string;
  /** Heights of the sheets above the tile tops, in metres. */
  heights: number[];
  /** Quarter turns of the whole stack, so the two stacks' patterns and drifts differ. */
  turn: number;
  /** Sideways shift (x, z) of each sheet, metres, so the stack reads as one soft body. */
  shifts: [number, number][];
  /** Sheet alpha at full density. */
  alpha: number;
  /** Lattice cells across the two noise textures: fewer cells means bigger billows. */
  billows: number;
  wisps: number;
  /** UV scroll per second for the billows (u, v) and the wisps (u, v). */
  drift: [number, number, number, number];
  /** Texture repeats across a sheet: more means finer detail. */
  scale: number;
}

const STACKS: Stack[] = [
  {
    name: 'boardFogLow',
    heights: [0.005, 0.014, 0.024],
    turn: 0,
    shifts: [
      [0, 0],
      [0.06, -0.04],
      [-0.05, 0.07],
    ],
    alpha: 0.95,
    billows: 4,
    wisps: 5,
    drift: [0.01, 0.004, -0.006, 0.008],
    scale: 1,
  },
  {
    name: 'boardFogHigh',
    heights: [0.034, 0.045, 0.056],
    turn: 1,
    shifts: [
      [0, 0],
      [-0.07, 0.05],
      [0.05, -0.06],
    ],
    alpha: 0.55,
    billows: 5,
    wisps: 4,
    drift: [0.007, -0.012, -0.011, -0.003],
    scale: 1.3,
  },
];

/** Wide enough to reach past the board's frame; the soft edge ends before the table's rim. */
const SHEET_SIZE = 1.4;
const SHEET_SUBDIVISIONS = 12;
/** Full thickness inside this distance from the centre (a rounded square, metres) … */
const FULL_RADIUS = 0.38;
/** … and gone by this one: past the board's edge, short of the tablets and the table's rim. */
const EDGE_RADIUS = 0.58;
const NOISE_SIZE = 128;
const NOISE_OCTAVES = 3;
/** Dark enough that the sun and sky light it to a mid grey rather than a white haze … */
const SMOKE_COLOR = new Color3(0.38, 0.38, 0.42);
/** … with a little glow so the thick parts read as lit from within on the night side. */
const SMOKE_GLOW = new Color3(0.07, 0.07, 0.09);
interface Shape {
  /** The least alpha anywhere: above zero for a field that only modulates another. */
  floor: number;
  /** Noise values below `low` are clear and above `high` solid. */
  low: number;
  high: number;
  /** Brightness of the thinnest smoke; the thickest is white, as if lit from above. */
  shade: number;
}

/** The billows field is shaped hard (clear gaps, solid lumps); the wisps field only modulates. */
const BILLOWS_SHAPE: Shape = { floor: 0, low: 0.3, high: 0.78, shade: 0.3 };
const WISPS_SHAPE: Shape = { floor: 0.5, low: 0.3, high: 0.8, shade: 0.6 };

interface Sheets {
  mesh: Mesh;
  material: StandardMaterial;
  billows: Texture;
  wisps: Texture;
  stack: Stack;
}

export function createBoardFog(scene: Scene, parent: TransformNode | null = null): BoardFog {
  const stacks = STACKS.map((stack, i) => createSheets(scene, stack, i, parent));
  let enabled = false;
  let density = DEFAULT_FOG_DENSITY;
  let time = 0;

  const apply = () => {
    for (const { mesh, material, stack } of stacks) {
      mesh.setEnabled(enabled && density > 0);
      material.alpha = stack.alpha * density;
    }
  };
  apply();

  return {
    setEnabled(on) {
      enabled = on;
      apply();
    },
    setDensity(d) {
      density = Math.max(0, Math.min(1, d));
      apply();
    },
    update(dt) {
      if (!enabled || density <= 0) return;
      time += dt;
      for (const { billows, wisps, stack } of stacks) {
        const [bu, bv, wu, wv] = stack.drift;
        // The textures tile, so the offsets can wrap instead of growing for ever.
        billows.uOffset = frac(time * bu);
        billows.vOffset = frac(time * bv);
        wisps.uOffset = frac(time * wu);
        wisps.vOffset = frac(time * wv);
      }
    },
    dispose() {
      for (const { mesh } of stacks) mesh.dispose(false, true);
    },
  };
}

function createSheets(
  scene: Scene,
  stack: Stack,
  index: number,
  parent: TransformNode | null,
): Sheets {
  const mesh = MeshBuilder.CreateGround(
    stack.name,
    { width: SHEET_SIZE, height: SHEET_SIZE, subdivisions: SHEET_SUBDIVISIONS },
    scene,
  );
  mesh.parent = parent;
  mesh.isPickable = false;
  mesh.hasVertexAlpha = true;
  mesh.setVerticesData(VertexBuffer.ColorKind, edgeFalloff(mesh), false, 4);

  // The mesh stays at the origin and the instances carry the board position, like the tiles,
  // so the turntable's pivot turns the smoke with the board.
  const matrices = new Float32Array(stack.heights.length * 16);
  stack.heights.forEach((height, i) => {
    const [dx, dz] = stack.shifts[i] ?? [0, 0];
    Matrix.RotationY((Math.PI / 2) * stack.turn)
      .multiply(Matrix.Translation(BOARD_CENTER.x + dx, BOARD_TOP_Y + height, BOARD_CENTER.z + dz))
      .copyToArray(matrices, i * 16);
  });
  mesh.thinInstanceSetBuffer('matrix', matrices, 16, true);

  const billows = createSmokeTexture(
    scene,
    `${stack.name}Billows`,
    stack.billows,
    BILLOWS_SHAPE,
    index * 2 + 1,
  );
  const wisps = createSmokeTexture(
    scene,
    `${stack.name}Wisps`,
    stack.wisps,
    WISPS_SHAPE,
    index * 2 + 2,
  );
  for (const texture of [billows, wisps]) {
    texture.uScale = stack.scale;
    texture.vScale = stack.scale;
  }
  const material = new StandardMaterial(`${stack.name}Material`, scene);
  material.diffuseTexture = billows;
  material.useAlphaFromDiffuseTexture = true;
  material.opacityTexture = wisps;
  material.diffuseColor = SMOKE_COLOR;
  material.emissiveColor = SMOKE_GLOW;
  material.specularColor = Color3.Black();
  material.backFaceCulling = false;
  material.disableDepthWrite = true;
  mesh.material = material;
  return { mesh, material, billows, wisps, stack };
}

/** Per-vertex alpha: full over the middle of the board, gone a little past its edge. */
function edgeFalloff(mesh: Mesh): Float32Array {
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind)!;
  const count = positions.length / 3;
  const colors = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    const x = positions[i * 3]!;
    const z = positions[i * 3 + 2]!;
    // A rounded square follows the board's shape better than a circle would.
    const d = Math.pow(x ** 4 + z ** 4, 0.25);
    colors.set([1, 1, 1, 1 - smoothstep(FULL_RADIUS, EDGE_RADIUS, d)], i * 4);
  }
  return colors;
}

/** Tileable smoke texture: value noise shaped into billows in the alpha channel and shaded. */
function createSmokeTexture(
  scene: Scene,
  name: string,
  cells: number,
  shape: Shape,
  seed: number,
): Texture {
  const noise = tileableValueNoise(NOISE_SIZE, cells, NOISE_OCTAVES, seed);
  const data = new Uint8Array(NOISE_SIZE * NOISE_SIZE * 4);
  for (let i = 0; i < noise.length; i++) {
    const v = noise[i]!;
    const alpha = shape.floor + (1 - shape.floor) * smoothstep(shape.low, shape.high, v);
    const shade = Math.round(255 * (shape.shade + (1 - shape.shade) * v));
    data.set([shade, shade, shade, Math.round(255 * alpha)], i * 4);
  }
  const texture = RawTexture.CreateRGBATexture(
    data,
    NOISE_SIZE,
    NOISE_SIZE,
    scene,
    true,
    false,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  texture.name = name;
  texture.hasAlpha = true;
  texture.wrapU = Texture.WRAP_ADDRESSMODE;
  texture.wrapV = Texture.WRAP_ADDRESSMODE;
  // Scrolling would otherwise flag the material dirty every frame.
  texture.optimizeUVAllocation = false;
  return texture;
}

/**
 * Value noise that wraps: `octaves` lattices of random values, the first `cells` across and each
 * next one twice as fine at half the weight, smoothly interpolated modulo the lattice so the
 * texture repeats without a seam. Stretched to 0…1 so the shaping above sees the full range.
 */
function tileableValueNoise(size: number, cells: number, octaves: number, seed: number) {
  const rand = mulberry32(seed);
  const out = new Float32Array(size * size);
  for (let o = 0; o < octaves; o++) {
    const n = cells << o;
    const weight = 1 / (1 << o);
    const lattice = new Float32Array(n * n);
    for (let i = 0; i < lattice.length; i++) lattice[i] = rand();
    for (let y = 0; y < size; y++) {
      const fy = (y / size) * n;
      const y0 = Math.floor(fy);
      const y1 = (y0 + 1) % n;
      const ty = fade(fy - y0);
      for (let x = 0; x < size; x++) {
        const fx = (x / size) * n;
        const x0 = Math.floor(fx);
        const x1 = (x0 + 1) % n;
        const tx = fade(fx - x0);
        const top = lerp(lattice[y0 * n + x0]!, lattice[y0 * n + x1]!, tx);
        const bottom = lerp(lattice[y1 * n + x0]!, lattice[y1 * n + x1]!, tx);
        const i = y * size + x;
        out[i] = (out[i] ?? 0) + weight * lerp(top, bottom, ty);
      }
    }
  }
  let min = Infinity;
  let max = -Infinity;
  for (const v of out) {
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  for (let i = 0; i < out.length; i++) out[i] = (out[i]! - min) / (max - min || 1);
  return out;
}

/** Deterministic, so the smoke looks the same on every run (and in screenshots). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function frac(x: number): number {
  return x - Math.floor(x);
}
